/**
 * Authentication Hook
 * Provides authentication utilities throughout the app
 */

import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useRecoilValue } from 'recoil';
import { loginRequest } from './authConfig';
import { tokenState, userInfoState } from './authState';

export const useAuth = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const token = useRecoilValue(tokenState);
  const userInfo = useRecoilValue(userInfoState);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }, [instance, accounts]);

  return {
    isAuthenticated,
    token,
    userInfo,
    login,
    logout,
    getAccessToken,
  };
};
